import { type ReactElement } from 'react'

import type { FilteringRuleRead, RuleExamplesRead } from '@/client'
import { useFilteringRules } from '@/components/features/use-filtering-rules'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Textarea } from '@/components/ui/textarea'

function DeleteRuleAction({
  ruleId,
  trigger,
  pending,
  onDelete,
}: {
  ruleId: string
  trigger: string
  pending: boolean
  onDelete: (ruleId: string) => void
}): ReactElement {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button type="button" variant="destructive" disabled={pending}>
          {trigger}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this rule permanently?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently deletes the rule and its authoring provenance. Historical Browsing activity
            remains unchanged.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => onDelete(ruleId)} disabled={pending}>
            Delete permanently
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function ExampleList({ title, examples }: { title: string; examples: string[] }): ReactElement {
  return (
    <div className="space-y-1">
      <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">{title}</p>
      <ul className="space-y-1 text-sm">
        {examples.map((example) => (
          <li key={example} className="break-all font-mono text-xs">
            {example}
          </li>
        ))}
      </ul>
    </div>
  )
}

function RuleExamples({ title, examples }: { title: string; examples: RuleExamplesRead }): ReactElement {
  return (
    <div className="space-y-2">
      <h5 className="text-sm font-semibold">{title}</h5>
      <div className="grid gap-3 sm:grid-cols-2">
        <ExampleList title="Should match" examples={examples.should_match} />
        <ExampleList title="Should not match" examples={examples.should_not_match} />
      </div>
    </div>
  )
}

function RulePredicate({ predicate }: { predicate: FilteringRuleRead['predicate'] }): ReactElement {
  if (predicate.kind === 'video') {
    return (
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Provider</dt>
        <dd className="font-mono">{predicate.provider}</dd>
        <dt className="text-muted-foreground">Video scope</dt>
        <dd>{predicate.video_scope === 'provider' ? 'Provider-wide' : 'Specific video'}</dd>
        {predicate.video_id !== null && (
          <>
            <dt className="text-muted-foreground">Video ID</dt>
            <dd className="font-mono">{predicate.video_id}</dd>
          </>
        )}
        {predicate.source_url !== null && (
          <>
            <dt className="text-muted-foreground">Source URL</dt>
            <dd className="break-all font-mono">{predicate.source_url}</dd>
          </>
        )}
      </dl>
    )
  }

  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
      <dt className="text-muted-foreground">Host</dt>
      <dd className="font-mono">{predicate.host}</dd>
      <dt className="text-muted-foreground">Subdomains</dt>
      <dd>{predicate.include_subdomains ? 'Included' : 'Named host only'}</dd>
      <dt className="text-muted-foreground">Path</dt>
      <dd className="font-mono">{predicate.path}</dd>
      <dt className="text-muted-foreground">Path mode</dt>
      <dd className="capitalize">{predicate.path_mode}</dd>
    </dl>
  )
}

function RuleCard({
  rule,
  replacesRule,
  onConfirm,
  onDelete,
  onReplace,
  onSetEnabled,
  pending,
}: {
  rule: FilteringRuleRead
  replacesRule?: FilteringRuleRead
  onConfirm?: (ruleId: string) => void
  onDelete?: (ruleId: string) => void
  onReplace?: (rule: FilteringRuleRead) => void
  onSetEnabled?: (ruleId: string, enabled: boolean) => void
  pending: boolean
}): ReactElement {
  return (
    <Card role="article" aria-label={rule.description}>
      <CardHeader className="space-y-2 pb-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={rule.action === 'block' ? 'destructive' : 'secondary'}>{rule.action}</Badge>
          <Badge variant="outline">{rule.status}</Badge>
        </div>
        <CardTitle className="text-base leading-6">{rule.description}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <RulePredicate predicate={rule.predicate} />
        {rule.status === 'proposed' && replacesRule && (
          <section aria-label="Replacement target" className="bg-muted space-y-2 rounded-md p-3">
            <p className="text-sm font-semibold">Replaces enabled rule</p>
            <p className="text-sm">{replacesRule.description}</p>
            <RulePredicate predicate={replacesRule.predicate} />
          </section>
        )}
        <RuleExamples title="Model examples" examples={rule.model_examples} />
        <RuleExamples title="Server boundary examples" examples={rule.server_examples} />
        {rule.status === 'proposed' && onConfirm && onDelete && (
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={() => onConfirm(rule.id)} disabled={pending}>
              Confirm proposal
            </Button>
            <DeleteRuleAction
              ruleId={rule.id}
              trigger="Discard proposal"
              pending={pending}
              onDelete={onDelete}
            />
          </div>
        )}
        {rule.status !== 'proposed' && onSetEnabled && onDelete && (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onSetEnabled(rule.id, rule.status === 'disabled')}
              disabled={pending}
            >
              {rule.status === 'enabled' ? 'Disable rule' : 'Enable rule'}
            </Button>
            {rule.status === 'enabled' && onReplace && (
              <Button
                type="button"
                variant="outline"
                onClick={() => onReplace(rule)}
                disabled={pending}
              >
                Replace rule
              </Button>
            )}
            <DeleteRuleAction
              ruleId={rule.id}
              trigger="Delete rule"
              pending={pending}
              onDelete={onDelete}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function FilteringRules({ deviceId }: { deviceId: string }): ReactElement {
  const {
    rules,
    rulesError,
    proposedRules,
    enabledRules,
    disabledRules,
    generatePending,
    form,
    clarificationForm,
    originalIntent,
    clarification,
    clarificationAnswers,
    submitIntent,
    submitClarification,
    restartClarification,
    replacementTarget,
    startReplacement,
    cancelReplacement,
    existingRule,
    conflict,
    conflictingRule,
    requestedRule,
    conflictCanSwap,
    conflictDialogOpen,
    setConflictDialogOpen,
    confirmRule,
    deleteRule,
    setRuleEnabled,
    setEnabledPending,
    mutationPending,
    mutationFailed,
  } = useFilteringRules({ source: 'admin', deviceId })

  return (
    <section className="space-y-5" aria-labelledby="filtering-rules-heading">
      <div className="space-y-2">
        <h2 id="filtering-rules-heading" className="text-lg font-semibold">
          Filtering rules
        </h2>
        <div className="text-muted-foreground space-y-1 text-sm">
          <p>Filtering reacts after a page loads and fails open when submission fails.</p>
          <p>Unmatched URLs are allowed by default, so allow rules matter only when they outrank a block.</p>
          <p>
            Paths are case-sensitive. Query parameters and fragments are ignored. Rules use exact paths or
            slash-delimited subtrees, never wildcards.
          </p>
          <p>Video rules cover recognized top-level YouTube video URLs only.</p>
          <p>The server does not look up titles, thumbnails, existence, or metadata.</p>
        </div>
      </div>

      {replacementTarget && (
        <div role="status" className="border-border space-y-2 rounded-md border p-3 text-sm">
          <p>
            Replacing <span className="font-medium">{replacementTarget.description}</span>. Generate and
            review the new meaning before confirming.
          </p>
          <Button type="button" variant="outline" onClick={cancelReplacement}>
            Cancel replacement
          </Button>
        </div>
      )}

      {originalIntent === null && (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(submitIntent)} className="space-y-3">
            <FormField
              control={form.control}
              name="intent_text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Filtering intent</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Block YouTube Shorts on this device"
                      rows={3}
                      maxLength={500}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={generatePending}>
              {generatePending ? 'Generating…' : 'Generate proposal'}
            </Button>
          </form>
        </Form>
      )}

      {originalIntent !== null && (
        <section
          aria-labelledby="filtering-clarification-heading"
          className="border-border space-y-4 rounded-lg border p-4"
        >
          <div className="space-y-1">
            <h3 id="filtering-clarification-heading" className="font-semibold">
              Clarification needed
            </h3>
            <p className="text-muted-foreground text-sm">Original intent</p>
            <p className="text-sm">{originalIntent}</p>
          </div>

          {clarificationAnswers.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Clarification history</h4>
              <ol className="space-y-2">
                {clarificationAnswers.map((answer, index) => (
                  <li key={`${index}-${answer.question}`} className="bg-muted rounded-md p-3 text-sm">
                    <p className="font-medium">
                      Question {index + 1}: {answer.question}
                    </p>
                    <p>Answer: {answer.answer}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {clarification && (
            <div className="space-y-3">
              <p className="font-medium">{clarification.question}</p>
              <div className="flex flex-wrap gap-2">
                {clarification.choices.map((choice) => (
                  <Button
                    key={choice}
                    type="button"
                    variant="outline"
                    disabled={generatePending}
                    onClick={() => submitClarification(choice)}
                  >
                    {choice}
                  </Button>
                ))}
              </div>
              <Form {...clarificationForm}>
                <form
                  onSubmit={clarificationForm.handleSubmit((values) =>
                    submitClarification(values.answer),
                  )}
                  className="space-y-3"
                >
                  <FormField
                    control={clarificationForm.control}
                    name="answer"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Your answer</FormLabel>
                        <FormControl>
                          <Textarea {...field} rows={2} maxLength={500} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" disabled={generatePending}>
                    Submit answer
                  </Button>
                </form>
              </Form>
            </div>
          )}

          {generatePending && (
            <p role="status" className="text-muted-foreground text-sm">
              Generating the next step…
            </p>
          )}
          <Button
            type="button"
            variant="outline"
            disabled={generatePending}
            onClick={restartClarification}
          >
            Start over
          </Button>
        </section>
      )}

      {existingRule && (
        <div role="status" className="border-border space-y-3 rounded-md border p-4 text-sm">
          <p>
            <span className="font-medium">{existingRule.rule.description}</span>{' '}
            {existingRule.recommended_action === 'none'
              ? 'This rule is already enabled. No changes are needed.'
              : 'This rule already exists but is disabled.'}
          </p>
          {existingRule.recommended_action === 'enable' && (
            <Button
              type="button"
              variant="outline"
              disabled={setEnabledPending}
              onClick={() => setRuleEnabled(existingRule.rule.id, true)}
            >
              Enable existing rule
            </Button>
          )}
        </div>
      )}

      {conflictCanSwap && conflict && conflictingRule && requestedRule && (
        <AlertDialog open={conflictDialogOpen} onOpenChange={setConflictDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Replace currently enabled rule?</AlertDialogTitle>
              <AlertDialogDescription>
                Enabling {requestedRule.description} will disable {conflictingRule.description}. Both
                changes happen together.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setRuleEnabled(requestedRule.id, true, conflict.conflicting_rule_id)
                }}
              >
                Swap enabled rule
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {conflict && !conflictCanSwap && (
        <p role="alert" className="text-destructive text-sm">
          Cannot enable this rule because {conflictingRule?.description ?? 'another rule'} (
          {conflict.conflicting_rule_id}) is already enabled. No rules were changed.
        </p>
      )}
      {mutationFailed && (
        <p role="alert" className="text-destructive text-sm">
          The Filtering rule request failed. Review the intent and try again.
        </p>
      )}
      {rulesError && (
        <p role="alert" className="text-destructive text-sm">
          Filtering rules could not be loaded.
        </p>
      )}

      <section aria-labelledby="rule-proposals-heading">
        <h3 id="rule-proposals-heading" className="mb-3 font-semibold">
          Rule proposals
        </h3>
        <div className="space-y-3">
          {proposedRules.length === 0 ? (
            <p className="text-muted-foreground text-sm">No proposals.</p>
          ) : (
            proposedRules.map((rule) => (
              <RuleCard
                key={rule.id}
                rule={rule}
                replacesRule={rules.find((candidate) => candidate.id === rule.replaces_rule_id)}
                pending={mutationPending}
                onConfirm={confirmRule}
                onDelete={deleteRule}
              />
            ))
          )}
        </div>
      </section>

      <section aria-labelledby="enabled-rules-heading">
        <h3 id="enabled-rules-heading" className="mb-3 font-semibold">
          Enabled rules
        </h3>
        <div className="space-y-3">
          {enabledRules.length === 0 ? (
            <p className="text-muted-foreground text-sm">No enabled rules.</p>
          ) : (
            enabledRules.map((rule) => (
              <RuleCard
                key={rule.id}
                rule={rule}
                pending={mutationPending}
                onDelete={deleteRule}
                onReplace={startReplacement}
                onSetEnabled={setRuleEnabled}
              />
            ))
          )}
        </div>
      </section>

      <section aria-labelledby="disabled-rules-heading">
        <h3 id="disabled-rules-heading" className="mb-3 font-semibold">
          Disabled rules
        </h3>
        <div className="space-y-3">
          {disabledRules.length === 0 ? (
            <p className="text-muted-foreground text-sm">No disabled rules.</p>
          ) : (
            disabledRules.map((rule) => (
              <RuleCard
                key={rule.id}
                rule={rule}
                pending={mutationPending}
                onDelete={deleteRule}
                onSetEnabled={setRuleEnabled}
              />
            ))
          )}
        </div>
      </section>
    </section>
  )
}
